import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  createFilterOptions,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { cloneElement, useEffect, useRef, useState } from 'react'
import { useGetList, useGetOne, useGetRecordRepresentation, useTranslate } from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import useBatchedGetMany from './useBatchedGetMany'
import {
  SYSTEM_PERMISSION_CREATE,
  SYSTEM_PERMISSION_LIST,
  SYSTEM_PERMISSION_READ,
  SYSTEM_PERMISSION_SHOW,
} from '../../config/permissions'
import { useResourcePermissions } from '../../domain/permissions'
import { useSmallScreen } from '../../utils/theme'
import { useCreateResource } from '../common/create'
import UserGroupFormLayout, { transformValues as transformUserGroup } from '../userGroups/form'
import { UserSuspendedChip } from '../users/suspension'
import VehicleGroupFormLayout, { transformValues as transformVehicleGroup } from '../vehicleGroups/form'

const secondaryText = (reference, record) => {
  if (reference === 'users') return record.email
  if (reference === 'vehicles') return [record.brand, record.model].filter(Boolean).join(' ')
  return record.description
}

const displayName = (reference, record, getRepresentation) =>
  reference === 'vehicles' ? record.designation || getRepresentation(record) : getRepresentation(record)

const memberControlSx = {
  width: '100%',
  minWidth: 0,
  m: 0,
  '& .MuiTextField-root': { m: 0, width: '100%' },
  '& .MuiOutlinedInput-root': { height: 40 },
}

const memberControlsLayoutSx = {
  mx: 2,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: 1,
  alignItems: 'center',
}

const MEMBER_BATCH_SIZE = 50
const CREATE_GROUP_OPTION = { id: 'create-group' }
const filterChoices = createFilterOptions()

const CreateLinkedGroupPicker = ({ reference, onCreated, children }) => {
  const [canCreate] = useResourcePermissions(reference, SYSTEM_PERMISSION_CREATE)
  const isUserGroup = reference === 'user-groups'
  const [openCreate, createDialog] = useCreateResource(
    reference,
    isUserGroup ? <UserGroupFormLayout /> : <VehicleGroupFormLayout />,
    isUserGroup ? transformUserGroup : transformVehicleGroup,
    undefined,
    onCreated,
  )

  return (
    <>
      {children(canCreate ? () => openCreate({}) : null)}
      {createDialog}
    </>
  )
}

const LinkedGroupMembersTooltip = ({ reference, id, name, onSelectGroup, children }) => {
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const [open, setOpen] = useState(false)
  const memberResource = reference === 'user-groups' ? 'users' : 'vehicles'
  const memberSource = reference === 'user-groups' ? 'user_ids' : 'vehicle_ids'
  const [canReadMembers] = useResourcePermissions(memberResource, SYSTEM_PERMISSION_READ)
  const [canReadGroup, canListGroup] = useResourcePermissions(reference, SYSTEM_PERMISSION_READ, SYSTEM_PERMISSION_LIST)
  const canOpenGroup = canReadGroup && canListGroup
  const getMemberRepresentation = useGetRecordRepresentation(memberResource)
  const { data: group, error: groupError } = useGetOne(reference, { id }, { enabled: open && canReadMembers === true })
  const memberIds = group?.[memberSource] || []
  const { data: members, error: membersError } = useBatchedGetMany(memberResource, memberIds, {
    enabled: open && canReadMembers === true && memberIds.length > 0,
  })

  if (!canReadMembers) return children

  const preview = (
    <Box>
      <Typography variant="subtitle1" sx={{ px: 2, py: 1.5, fontWeight: 600 }}>
        {translate(`resources.${memberResource}.name`, 2)} · {memberIds.length}
      </Typography>
      <Divider />
      <Box sx={{ maxHeight: 320, overflowY: 'auto', px: 2, py: 1 }}>
        {groupError || membersError ? (
          <Typography variant="body2">{(groupError || membersError).message}</Typography>
        ) : !group || (memberIds.length > 0 && members.length === 0) ? (
          <CircularProgress size={20} color="inherit" />
        ) : memberIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {translate('mymove.accessGroups.noMembers')}
          </Typography>
        ) : (
          members.map((member) => (
            <Stack key={member.id} sx={{ py: 1 }}>
              <Typography variant="body1">{displayName(memberResource, member, getMemberRepresentation)}</Typography>
              {secondaryText(memberResource, member) && (
                <Typography variant="body2" color="text.secondary">
                  {secondaryText(memberResource, member)}
                </Typography>
              )}
              {memberResource === 'users' && member.user_account_is_suspended && (
                <Box sx={{ mt: 0.5 }}>
                  <UserSuspendedChip />
                </Box>
              )}
            </Stack>
          ))
        )}
      </Box>
    </Box>
  )

  if (isSmallScreen) {
    return (
      <>
        {cloneElement(children, {
          onClick: (event) => {
            if (!event.target.closest('button')) setOpen(true)
          },
          onKeyDown: (event) => {
            if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
              event.preventDefault()
              setOpen(true)
            }
          },
          tabIndex: 0,
          'aria-label': translate('mymove.accessGroups.previewMembers', { name }),
          sx: { ...children.props.sx, cursor: 'pointer' },
        })}
        <Drawer
          anchor="bottom"
          open={open}
          onClose={() => setOpen(false)}
          PaperProps={{ sx: { maxHeight: '75vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, p: 2 } }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{name}</Typography>
            <IconButton aria-label={translate('ra.action.close')} onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          {preview}
          {canOpenGroup && (
            <Button
              onClick={() => {
                setOpen(false)
                onSelectGroup(reference, id)
              }}
            >
              {translate('mymove.accessGroups.openGroup')}
            </Button>
          )}
        </Drawer>
      </>
    )
  }

  return (
    <Tooltip
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: 1,
            borderColor: 'divider',
            boxShadow: 6,
            maxWidth: 'none',
            width: { xs: 280, sm: 380 },
            p: 0,
          },
        },
      }}
      title={preview}
    >
      {children}
    </Tooltip>
  )
}

const MembershipColumn = ({ source, reference, label, canEdit, searchable = false, onSelectGroup }) => {
  const translate = useTranslate()
  const isSmallScreen = useSmallScreen()
  const getRepresentation = useGetRecordRepresentation(reference)
  const [canShow] = useResourcePermissions(reference, SYSTEM_PERMISSION_SHOW)
  const [canReadGroup, canListGroup] = useResourcePermissions(reference, SYSTEM_PERMISSION_READ, SYSTEM_PERMISSION_LIST)
  const canOpenGroup = canReadGroup && canListGroup
  const [canPreviewMembers] = useResourcePermissions(
    reference === 'user-groups' ? 'users' : reference === 'vehicle-groups' ? 'vehicles' : reference,
    SYSTEM_PERMISSION_READ,
  )
  const { setValue } = useFormContext()
  const ids = useWatch({ name: source }) || []
  const [search, setSearch] = useState('')
  const [selectedSearch, setSelectedSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(MEMBER_BATCH_SIZE)
  const listRef = useRef(null)
  const loadMoreRef = useRef(null)
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(selectedSearch), 300)
    return () => clearTimeout(timeout)
  }, [selectedSearch])
  const searching = searchable && Boolean(debouncedSearch.trim())
  const displayedIds = searching ? ids : ids.slice(0, visibleCount)
  const {
    data: selected = [],
    isPending: selectedPending,
    error: selectedError,
  } = useBatchedGetMany(reference, displayedIds, {
    search: searchable ? debouncedSearch : '',
  })
  useEffect(() => {
    if (searching || selectedSearch.trim() || selectedPending || selectedError || ids.length <= visibleCount) return
    const target = loadMoreRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleCount((count) => count + MEMBER_BATCH_SIZE)
      },
      { root: isSmallScreen ? null : listRef.current },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [searching, selectedSearch, selectedPending, selectedError, ids.length, visibleCount, isSmallScreen])
  const { data: choices = [], isPending: choicesPending } = useGetList(
    reference,
    {
      pagination: { page: 1, perPage: 25 },
      sort: { field: 'id', order: 'ASC' },
      filter: search ? (reference.endsWith('-groups') ? { name: search } : { search_designation: search }) : {},
    },
    { enabled: canEdit },
  )
  const recordsById = new Map(selected.map((record) => [String(record.id), record]))
  const availableChoices = choices.filter((choice) => !ids.some((id) => String(id) === String(choice.id)))
  const visibleIds = displayedIds.filter((id) => recordsById.has(String(id)))
  const emptyMessage = reference.endsWith('-groups') ? 'noLinkedGroups' : 'noMembers'
  const createGroupLabel = translate(`resources.${reference}.forms.create.title`)

  const updateIds = (nextIds) => setValue(source, nextIds, { shouldDirty: true, shouldValidate: true })
  const onCreated = (group) => {
    updateIds([...ids, group.id])
    setSearch('')
    setPickerOpen(false)
  }
  const selectedSearchInput = searchable && ids.length > 0 && (
    <TextField
      size="small"
      label={translate(`mymove.accessGroups.${reference === 'users' ? 'searchGroupUsers' : 'searchGroupVehicles'}`)}
      value={selectedSearch}
      onChange={(event) => setSelectedSearch(event.target.value)}
      sx={memberControlSx}
    />
  )

  const renderPicker = (onCreate) => (
    <>
      {canEdit && isSmallScreen && (
        <Dialog fullScreen open={pickerOpen} onClose={() => setPickerOpen(false)}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {translate('mymove.accessGroups.add', { name: translate(`resources.${reference}.name`, 1) })}
            <IconButton aria-label={translate('ra.action.close')} onClick={() => setPickerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              autoFocus
              label={translate('ra.action.search')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <List sx={{ pl: 1 }}>
              {onCreate && (
                <ListItemButton onClick={onCreate} sx={{ pl: 0, color: 'primary.main' }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                  <ListItemText primary={createGroupLabel} />
                </ListItemButton>
              )}
              {choicesPending ? (
                <CircularProgress size={24} />
              ) : availableChoices.length === 0 ? (
                <Typography color="text.secondary">{translate('ra.navigation.no_results')}</Typography>
              ) : (
                availableChoices.map((choice) => (
                  <ListItemButton
                    key={choice.id}
                    onClick={() => {
                      updateIds([...ids, choice.id])
                      setPickerOpen(false)
                      setSearch('')
                    }}
                  >
                    <ListItemText
                      primary={displayName(reference, choice, getRepresentation)}
                      secondary={secondaryText(reference, choice)}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </DialogContent>
        </Dialog>
      )}
      {!isSmallScreen && (canEdit || selectedSearchInput) && (
        <Box sx={memberControlsLayoutSx}>
          {canEdit && (
            <Autocomplete
              sx={memberControlSx}
              slotProps={{ listbox: { sx: { pl: 1 } } }}
              options={availableChoices}
              filterOptions={(options, state) => {
                const matchingChoices = filterChoices(options, state)
                return onCreate ? [...matchingChoices, CREATE_GROUP_OPTION] : matchingChoices
              }}
              loading={choicesPending}
              value={null}
              inputValue={search}
              getOptionLabel={(option) =>
                option === CREATE_GROUP_OPTION ? createGroupLabel : String(getRepresentation(option) || '')
              }
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setSearch(value)
              }}
              onChange={(_, choice) => {
                if (choice === CREATE_GROUP_OPTION) onCreate()
                else if (choice) updateIds([...ids, choice.id])
                setSearch('')
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={option === CREATE_GROUP_OPTION ? { ...props.style, paddingLeft: 0 } : props.style}
                >
                  {option === CREATE_GROUP_OPTION ? (
                    <Stack direction="row" alignItems="center" sx={{ color: 'primary.main' }}>
                      <AddIcon sx={{ fontSize: 16 }} />
                      <span>{createGroupLabel}</span>
                    </Stack>
                  ) : (
                    getRepresentation(option)
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={translate('mymove.accessGroups.add', { name: translate(`resources.${reference}.name`, 1) })}
                />
              )}
            />
          )}
          {selectedSearchInput}
        </Box>
      )}
    </>
  )

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary" sx={{ px: { xs: 2, md: 0 } }}>
        {label} · {ids.length}
      </Typography>
      {isSmallScreen && (canEdit || selectedSearchInput) && (
        <Box sx={memberControlsLayoutSx}>
          {canEdit && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setPickerOpen(true)}
              sx={{ ...memberControlSx, height: 40 }}
            >
              {translate('mymove.accessGroups.add', { name: translate(`resources.${reference}.name`, 1) })}
            </Button>
          )}
          {selectedSearchInput}
        </Box>
      )}
      {reference.endsWith('-groups') && canEdit ? (
        <CreateLinkedGroupPicker reference={reference} onCreated={onCreated}>
          {renderPicker}
        </CreateLinkedGroupPicker>
      ) : (
        renderPicker(null)
      )}
      <Box
        ref={listRef}
        data-testid={`membership-list-${source}`}
        sx={{
          height: isSmallScreen ? 'auto' : 320,
          overflowY: isSmallScreen ? 'visible' : 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        {selectedError ? (
          <Typography color="error">{selectedError.message}</Typography>
        ) : (selectedSearch.trim() !== debouncedSearch.trim() || (searching && selectedPending)) &&
          ids.length > 0 ? null : ids.length === 0 ? (
          <Typography color="text.secondary">{translate(`mymove.accessGroups.${emptyMessage}`)}</Typography>
        ) : visibleIds.length === 0 && !selectedPending ? (
          <Typography color="text.secondary">
            {translate(`mymove.accessGroups.${selectedSearch.trim() ? 'noMatchingMembers' : emptyMessage}`)}
          </Typography>
        ) : (
          <>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {visibleIds.map((id) => {
                const record = recordsById.get(String(id))
                if (!record) return null
                const name = displayName(reference, record, getRepresentation)

                const row = (
                  <Box
                    component="li"
                    key={id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 1,
                      px: 2,
                      borderBottom: 1,
                      borderColor: 'divider',
                      ...(isSmallScreen && reference.endsWith('-groups') && canPreviewMembers
                        ? {
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                            },
                          }
                        : {}),
                    }}
                  >
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        component={
                          reference.endsWith('-groups')
                            ? canOpenGroup && !isSmallScreen
                              ? 'button'
                              : 'span'
                            : canShow
                              ? Link
                              : 'span'
                        }
                        type={reference.endsWith('-groups') && canOpenGroup && !isSmallScreen ? 'button' : undefined}
                        to={!reference.endsWith('-groups') && canShow ? `/${reference}/${id}/show` : undefined}
                        onClick={
                          reference.endsWith('-groups') && canOpenGroup && !isSmallScreen
                            ? () => onSelectGroup(reference, id)
                            : undefined
                        }
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          ...(reference === 'users'
                            ? { overflowWrap: 'anywhere' }
                            : { overflow: 'hidden', textOverflow: 'ellipsis' }),
                          ...(reference.endsWith('-groups') && canOpenGroup && !isSmallScreen
                            ? {
                                border: 0,
                                p: 0,
                                bgcolor: 'transparent',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }
                            : {}),
                        }}
                      >
                        {name}
                      </Typography>
                      {secondaryText(reference, record) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap={reference !== 'users'}
                          sx={reference === 'users' ? { overflowWrap: 'anywhere' } : undefined}
                        >
                          {secondaryText(reference, record)}
                        </Typography>
                      )}
                      {reference === 'users' && record.user_account_is_suspended && (
                        <Box sx={{ mt: 0.5 }}>
                          <UserSuspendedChip />
                        </Box>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                      {isSmallScreen && reference.endsWith('-groups') && canPreviewMembers && (
                        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: 'primary.main' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {translate('mymove.accessGroups.viewMembers')}
                          </Typography>
                          <ChevronRightIcon fontSize="small" />
                        </Stack>
                      )}
                      {canEdit && (
                        <IconButton
                          size="small"
                          aria-label={translate('mymove.accessGroups.remove', { name })}
                          onClick={() => updateIds(ids.filter((selectedId) => String(selectedId) !== String(id)))}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                )

                return reference.endsWith('-groups') ? (
                  <LinkedGroupMembersTooltip
                    key={id}
                    reference={reference}
                    id={id}
                    name={name}
                    onSelectGroup={onSelectGroup}
                  >
                    {row}
                  </LinkedGroupMembersTooltip>
                ) : (
                  row
                )
              })}
            </Box>
            {selectedPending && <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 2 }} />}
            {!searching && !selectedSearch.trim() && ids.length > visibleCount && !selectedPending && (
              <Box ref={loadMoreRef} sx={{ height: 1 }} />
            )}
          </>
        )}
      </Box>
    </Stack>
  )
}

export default MembershipColumn
